import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import { config } from '../../../config';

export const PORTAL_RESCHEDULE_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'portal-reschedule',
} as const;

function getPortalRescheduleBotTarget(): string | { system: string; value: string } {
  const id = import.meta.env.VITE_MEDPLUM_PORTAL_RESCHEDULE_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_PORTAL_RESCHEDULE_BOT_IDENTIFIER_SYSTEM?.trim()
    ?? PORTAL_RESCHEDULE_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_PORTAL_RESCHEDULE_BOT_IDENTIFIER_VALUE?.trim()
    ?? PORTAL_RESCHEDULE_BOT_IDENTIFIER.value;
  return { system, value };
}

export interface SubmitPortalRescheduleBotResult {
  originalAppointmentId: string;
  proposedAppointmentId: string;
}

export async function submitPortalRescheduleViaBot(
  medplum: MedplumClient,
  input: {
    patientId: string;
    appointmentId: string;
    requestedStart: string;
    requestedEnd: string;
  },
): Promise<SubmitPortalRescheduleBotResult> {
  if (!config.medplum.clientId) {
    throw new Error('Medplum client is not configured.');
  }
  return medplum.executeBot(
    getPortalRescheduleBotTarget(),
    {
      action: 'submit-reschedule-request',
      ...input,
    },
    ContentType.JSON,
  ) as Promise<SubmitPortalRescheduleBotResult>;
}

export interface SubmitNewAppointmentBotResult {
  proposedAppointmentId: string;
  episodeOfCareId: string;
}

export async function submitNewAppointmentViaBot(
  medplum: MedplumClient,
  input: {
    patientId: string;
    practitionerId: string;
    episodeOfCareId: string;
    requestedStart: string;
    requestedEnd: string;
  },
): Promise<SubmitNewAppointmentBotResult> {
  if (!config.medplum.clientId) {
    throw new Error('Medplum client is not configured.');
  }
  return medplum.executeBot(
    getPortalRescheduleBotTarget(),
    {
      action: 'submit-new-appointment-request',
      ...input,
    },
    ContentType.JSON,
  ) as Promise<SubmitNewAppointmentBotResult>;
}

export interface RespondPortalRescheduleBotResult {
  proposedAppointmentId: string;
  originalAppointmentId?: string;
  decision: 'accept' | 'deny';
  finalStatus: 'booked' | 'cancelled';
}

export async function respondPortalRescheduleViaBot(
  medplum: MedplumClient,
  input: {
    proposedAppointmentId: string;
    decision: 'accept' | 'deny';
  },
): Promise<RespondPortalRescheduleBotResult> {
  return medplum.executeBot(
    getPortalRescheduleBotTarget(),
    {
      action: 'respond-reschedule-request',
      ...input,
    },
    ContentType.JSON,
  ) as Promise<RespondPortalRescheduleBotResult>;
}
